import React, { useCallback, useEffect, useRef, useState } from 'react';
import { API_URL } from './api';
import { readUberDirectResponse, UberDirectStatus } from './UberDirectSettings';
import './UberDirect.css';

const ENDPOINT = `${API_URL}/api/master/delivery/uber-partner`;

function UberDirectAccount({ account, platform, request, onUpdated }) {
  const [evidence, setEvidence] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [organizationId, setOrganizationId] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const active = useRef(false);
  const pending = useRef(false);
  useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);

  async function act(action, body) {
    if (pending.current) return;
    pending.current = true;
    setBusy(action);
    setError('');
    setNotice('');
    try {
      await request(`${ENDPOINT}/${encodeURIComponent(account.id)}/${action}`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then(readUberDirectResponse);
      if (!active.current) return;
      setEvidence('');
      setConfirmed(false);
      setOrganizationId('');
      setNotice(action === 'reconcile'
        ? 'Organisation rapprochée. Son activation doit encore être vérifiée.'
        : action === 'refresh' ? 'État vérifié auprès d’Uber. Cette lecture ne confirme pas à elle seule l’activation de la facturation.'
          : body.confirmed ? 'Confirmation d’activation enregistrée.' : 'Livraisons suspendues pour cet établissement.');
      await onUpdated();
    } catch (err) {
      if (active.current) setError(err.message);
    } finally {
      pending.current = false;
      if (active.current) setBusy('');
    }
  }

  const ready = account.status === 'ready' || !!account.activationConfirmedAt;
  const hasPartnerOrganization = account.mode === 'partner' && !!account.organizationId;
  const canConfirm = hasPartnerOrganization && !['creating', 'reconciliation_required'].includes(account.status)
    && !!account.checkedAt && platform.configured && platform.enabled;

  return <article className="uber-direct-account">
    <div className="uber-direct-heading"><h3>{account.name || `Établissement ${account.id}`}</h3><UberDirectStatus status={account.status} /></div>
    <dl className="uber-direct-facts">
      <div><dt>Contact Uber</dt><dd>{account.contactEmail || 'À renseigner'}</dd></div>
      <div><dt>Organisation Uber</dt><dd>{account.organizationId || 'Pas encore créée'}</dd></div>
      <div><dt>Facturation</dt><dd>{account.status === 'legacy' ? 'Compte individuel existant' : account.mode === 'partner' ? 'Uber → établissement' : 'Parcours partenaire à préparer'}</dd></div>
    </dl>
    {account.message && <p className="uber-direct-account-message">{account.message}</p>}
    {error && <p role="alert" className="uber-direct-alert is-error">{error}</p>}
    {notice && <p role="status" className="uber-direct-alert">{notice}</p>}
    {account.canRefresh && <div className="uber-direct-actions"><button type="button" className="uber-direct-button is-secondary" disabled={!!busy} onClick={() => act('refresh', {})}>{busy === 'refresh' ? 'Vérification…' : 'Vérifier ce compte auprès d’Uber'}</button></div>}
    {hasPartnerOrganization && <details className="uber-direct-operator-details">
      <summary>{ready ? 'Gérer l’activation des livraisons' : 'Confirmer l’activation après vérification Uber'}</summary>
      <p>Une organisation créée ou une invitation demandée ne prouve pas que la facturation est active. Vérifiez auprès d’Uber que les courses sont bien facturées et réglées directement par cet établissement.</p>
      {!ready && !account.checkedAt && <p className="uber-direct-alert">Vérifiez d’abord ce compte auprès d’Uber avant de confirmer son activation.</p>}
      <form onSubmit={(event) => { event.preventDefault(); if (!busy && confirmed && (ready || (canConfirm && evidence.trim().length >= 8))) act('activation', { confirmed: !ready, evidence: ready ? '' : evidence.trim() }); }}>
        {!ready && <><label className="uber-direct-field"><span>Référence de la vérification Uber (ticket ou confirmation)</span>
          <input value={evidence} required minLength={8} maxLength={500} onChange={(event) => setEvidence(event.target.value)} disabled={!!busy} placeholder="Référence, date et résultat de la vérification" />
        </label>
        <p className="uber-direct-hint">Ne saisissez aucune donnée bancaire ni identifiant secret.</p></>}
        <label className="uber-direct-consent"><input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} disabled={!!busy} />
          <span>{ready ? 'Je confirme la suspension des nouvelles livraisons pour cet établissement.' : 'J’ai vérifié auprès d’Uber que la facturation directe de cet établissement et son accès aux livraisons sont actifs.'}</span>
        </label>
        <button type="submit" className={`uber-direct-button ${ready ? 'is-secondary' : ''}`} disabled={!!busy || !confirmed || (!ready && (evidence.trim().length < 8 || !canConfirm))}>
          {busy === 'activation' ? 'Enregistrement…' : ready ? 'Suspendre les livraisons' : 'Confirmer et activer les livraisons'}
        </button>
      </form>
    </details>}
    {account.status === 'reconciliation_required' && <details className="uber-direct-operator-details">
      <summary>Rapprocher une création au résultat incertain</summary>
      <p>Recherchez l’organisation dans le compte partenaire Uber, puis renseignez son identifiant. La vérification contrôle son rattachement ; elle ne crée pas une nouvelle organisation et n’active pas les livraisons.</p>
      <form onSubmit={(event) => { event.preventDefault(); if (!busy && organizationId.trim()) act('reconcile', { organizationId: organizationId.trim() }); }}>
        <label className="uber-direct-field"><span>Identifiant de l’organisation Uber à vérifier</span><input required value={organizationId} onChange={(event) => setOrganizationId(event.target.value)} disabled={!!busy} /></label>
        <button type="submit" className="uber-direct-button is-secondary" disabled={!!busy || !organizationId.trim() || !platform.configured}>{busy === 'reconcile' ? 'Vérification auprès d’Uber…' : 'Vérifier et rapprocher cette organisation'}</button>
      </form>
    </details>}
  </article>;
}

function UberDirectOverviewPanel({ token }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const active = useRef(false);
  const loadVersion = useRef(0);
  const request = useCallback((url, options = {}) => fetch(url, {
    ...options,
    headers: { ...options.headers, Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  }), [token]);
  const reload = useCallback(async () => {
    const version = ++loadVersion.current;
    setBusy(true);
    setError('');
    try {
      const payload = await request(ENDPOINT).then(readUberDirectResponse);
      if (active.current && version === loadVersion.current) setData(payload);
    } catch (err) {
      if (active.current && version === loadVersion.current) {
        setError(err.message);
        if ([401, 403].includes(err.status)) setData(null);
      }
    } finally {
      if (active.current && version === loadVersion.current) setBusy(false);
    }
  }, [request]);
  useEffect(() => {
    active.current = true;
    reload();
    return () => { active.current = false; };
  }, [reload]);

  const platform = data?.platform || {};
  const accounts = Array.isArray(data?.accounts) ? data.accounts : [];
  return <section className="uber-direct-panel" aria-labelledby="uber-direct-overview-title">
    <div className="uber-direct-heading">
      <div><span className="uber-direct-eyebrow">BECLICK · Compte partenaire BEQUANIM</span><h2 id="uber-direct-overview-title">Uber Direct — établissements</h2></div>
      <button type="button" className="uber-direct-button is-secondary" onClick={reload} disabled={busy}>{busy ? 'Chargement…' : 'Actualiser la liste'}</button>
    </div>
    <p className="uber-direct-intro">Les établissements complètent leur accès et leur moyen de paiement auprès d’Uber. Uber leur facture directement les livraisons. Aucune activation n’est déduite d’une simple invitation.</p>
    {error && <p className="uber-direct-alert is-error" role="alert">{error}</p>}
    {data && <>
      {(!platform.configured || !platform.enabled) && <p className="uber-direct-alert">{platform.message || 'Créez le compte partenaire BEQUANIM auprès d’Uber, puis configurez et activez son accès sur le serveur. Les invitations restent bloquées jusque-là.'}</p>}
      {platform.environment === 'sandbox' && <p className="uber-direct-alert">Environnement de test. Les confirmations affichées ici ne constituent pas une activation en production.</p>}
      <div className="uber-direct-summary"><span><strong>{accounts.length}</strong> établissements</span><span><strong>{accounts.filter((account) => account.status === 'ready').length}</strong> activés</span><span>Facturation directe par Uber</span></div>
      <div className="uber-direct-account-list">{accounts.map((account) => <UberDirectAccount key={account.id} account={account} platform={platform} request={request} onUpdated={reload} />)}</div>
      {!accounts.length && <p>Aucun établissement à afficher.</p>}
    </>}
  </section>;
}

export default function UberDirectOverview({ token }) {
  return token ? <UberDirectOverviewPanel key={token} token={token} /> : null;
}
